/** @jsxRuntime classic */
/** @jsx jsx */
import { jsx } from '@emotion/core';
import React, {
  FC,
  ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  Button,
  Checkbox,
  Empty,
  message,
  Modal,
  Popover,
  Tag,
  Tooltip,
} from 'antd';
import {
  CheckCircleOutlined,
  ClockCircleOutlined,
  DownloadOutlined,
  ExclamationCircleOutlined,
  FilterOutlined,
  CrownOutlined,
  ProfileOutlined,
  StarOutlined,
  StopOutlined,
} from '@ant-design/icons';
import { useIntl } from 'react-intl';
import { ThemedStyleFn, useStyles } from '~src/hooks/useStyles';
import { OffersEvaluationRow } from './OffersEvaluationRow';
import {
  buildMergedEvaluationVouchers,
  computeOfferSerials,
  EVALUATION_FILTER_OPTIONS,
  EVALUATION_OPTIONS,
  EvaluationContractLike,
  EvaluationFilterValue,
  EvaluationOfferLike,
  EvaluationValue,
  EvaluationVoucherLike,
  EvaluationVoucherResponseLike,
  getEvaluationBackendState,
  getEvaluationFilterValue,
  getEvaluationOption,
  getEvaluationValue,
  getOfferSerial,
  getOfferVendorName,
} from './evaluationData';
import {
  BulkOfferEntry,
  downloadAllOffersZip,
} from '~src/utils/downloader/downloadOfferZip';
import { useStore, DownloadingState } from '~src/store';
import {
  finalizeContractEvaluation,
  setContractOfferEvaluation,
} from '~src/services/api/offers';
import { useMutation, queryCache } from 'react-query';

type OffersEvaluationViewProps = {
  projectId: number;
  tenderId: number;
  contract: EvaluationContractLike;
  offers: EvaluationOfferLike[];
  vouchers: EvaluationVoucherLike[];
  voucherResponsesByOfferId?: Record<number, EvaluationVoucherResponseLike[]>;
  isEvaluationLocked?: boolean;
};

const DEFAULT_FILTER_VALUES: EvaluationFilterValue[] = [
  'unreviewed',
  'considered',
  'conditional',
  'selected',
  'priority',
  'rejected',
];

// Stable empty reference so rows without responses keep referential equality
// (otherwise a new [] every render would defeat React.memo on the row).
const EMPTY_RESPONSES: EvaluationVoucherResponseLike[] = [];

const FILTER_ICON_BY_VALUE: Record<EvaluationFilterValue, ReactNode> = {
  all: <FilterOutlined />,
  unreviewed: <ClockCircleOutlined />,
  considered: <StarOutlined />,
  conditional: <ExclamationCircleOutlined />,
  selected: <CheckCircleOutlined />,
  priority: <CrownOutlined />,
  rejected: <StopOutlined />,
};

const getFilterColor = (value: EvaluationFilterValue) => {
  if (value === 'all') return '#1890ff';

  const option = EVALUATION_OPTIONS.find(
    (item) => getEvaluationFilterValue(item.value) === value
  );

  return option?.color || '#8c8c8c';
};

export const OffersEvaluationView: FC<OffersEvaluationViewProps> = ({
  projectId,
  tenderId,
  contract,
  offers,
  vouchers,
  voucherResponsesByOfferId = {},
  isEvaluationLocked,
}) => {
  const styles = useStyles(styleFn);
  const intl = useIntl();

  const [filterValues, setFilterValues] = useState<EvaluationFilterValue[]>(
    DEFAULT_FILTER_VALUES
  );

  const [evaluationStateByOfferId, setEvaluationStateByOfferId] = useState<
    Record<number, EvaluationValue>
  >({});

  // Mirror state in a ref so the change handler can stay referentially stable
  // (it reads the previous value without depending on the state map).
  const evaluationStateRef = useRef(evaluationStateByOfferId);
  evaluationStateRef.current = evaluationStateByOfferId;

  const [isBulkDownloading, setIsBulkDownloading] = useState(false);

  const [setEvaluationMutation] = useMutation(
    ({ offerId, value }: { offerId: number; value: EvaluationValue }) =>
      setContractOfferEvaluation({
        projectId,
        tenderId,
        contractId: contract.id,
        offerId,
        body: {
          state: getEvaluationBackendState(value),
        },
      })
  );

  const [
    finalizeEvaluationMutation,
    { isLoading: isFinalizingEvaluation },
  ] = useMutation(
    () =>
      finalizeContractEvaluation({
        projectId,
        tenderId,
        contractId: contract.id,
      }),
    {
      onSuccess: () => {
        queryCache.invalidateQueries(['contracts', projectId, tenderId]);
        message.success(
          intl.formatMessage({
            id: 'evaluation-finalize-success',
          })
        );
      },
      onError: () => {
        message.error(
          intl.formatMessage({
            id: 'evaluation-finalize-error',
          })
        );
      },
    }
  );

  const contractOffers = useMemo(
    () => offers.filter((offer) => offer.contract_id === contract.id),
    [offers, contract.id]
  );

  const serialMap = useMemo(() => computeOfferSerials(contractOffers), [
    contractOffers,
  ]);

  const handleBulkDownload = async () => {
    setIsBulkDownloading(true);

    // Activate the download progress modal
    useStore.getState().setIsOfferDownload(true);
    useStore.getState().changeDownloadingActive(true);
    useStore.getState().changeDownloadingState(DownloadingState.PREPARING);

    try {
      const offersData: BulkOfferEntry[] = contractOffers.map((offer) => ({
        offerId: offer.id,
        offerName: `${getOfferSerial(offer.id, serialMap)} ${getOfferVendorName(
          offer,
          intl
        )}`,
        offerSerial: getOfferSerial(offer.id, serialMap),
        mergedVouchers: buildMergedEvaluationVouchers(
          vouchers,
          voucherResponsesByOfferId[offer.id] || [],
          intl
        ),
      }));
      await downloadAllOffersZip({
        projectId,
        tenderId,
        contractId: contract.id,
        contractName: contract.name || `Contract_${contract.id}`,
        offersData,
        tabLabel: 'Evaluation',
      });
    } catch {
      message.error(intl.formatMessage({ id: 'attachment-download-failed' }));
    } finally {
      setIsBulkDownloading(false);
      // Modal will auto-close after FINISHING state (handled by saveFilesToZip)
    }
  };

  useEffect(() => {
    const nextState = contractOffers.reduce<Record<number, EvaluationValue>>(
      (acc, offer) => {
        acc[offer.id] = getEvaluationValue(offer);
        return acc;
      },
      {}
    );

    setEvaluationStateByOfferId(nextState);
  }, [contractOffers]);

  const getOfferResponses = (offerId: number) => {
    return voucherResponsesByOfferId[offerId] || EMPTY_RESPONSES;
  };

  const isAllFiltersSelected =
    filterValues.length === DEFAULT_FILTER_VALUES.length;

  const handleToggleAllFilters = (checked: boolean) => {
    setFilterValues(checked ? DEFAULT_FILTER_VALUES : []);
  };

  const handleToggleFilter = (
    value: EvaluationFilterValue,
    checked: boolean
  ) => {
    if (value === 'all') {
      handleToggleAllFilters(checked);
      return;
    }

    setFilterValues((prev) => {
      if (checked) {
        return prev.includes(value) ? prev : [...prev, value];
      }

      return prev.filter((filterValue) => filterValue !== value);
    });
  };

  const filterButtonLabel = useMemo(() => {
    if (isAllFiltersSelected) {
      return intl.formatMessage({
        id: 'evaluation-filter-all-deliveries',
      });
    }

    if (!filterValues.length) {
      return intl.formatMessage({
        id: 'evaluation-filter-no-filters-selected',
      });
    }

    return EVALUATION_FILTER_OPTIONS.filter(
      (option) => option.value !== 'all' && filterValues.includes(option.value)
    )
      .map((option) =>
        intl.formatMessage({
          id: option.intlId,
        })
      )
      .join(', ');
  }, [filterValues, intl, isAllFiltersSelected]);

  const filteredOffers = useMemo(() => {
    if (isAllFiltersSelected) {
      return contractOffers;
    }

    return contractOffers.filter((offer) => {
      const evaluationValue = evaluationStateByOfferId[offer.id] ?? 0;
      const filterValue = getEvaluationFilterValue(evaluationValue);

      return filterValues.includes(filterValue);
    });
  }, [
    contractOffers,
    filterValues,
    isAllFiltersSelected,
    evaluationStateByOfferId,
  ]);

  const summary = useMemo(() => {
    return contractOffers.reduce(
      (acc, offer) => {
        const evaluationValue = evaluationStateByOfferId[offer.id] ?? 0;

        acc.total += 1;

        if (evaluationValue === 0) {
          acc.unreviewed += 1;
        }

        if (evaluationValue === 1 || evaluationValue === 2) {
          acc.inProgress += 1;
        }

        if (evaluationValue === 3 || evaluationValue === 5) {
          acc.accepted += 1;
        }

        if (evaluationValue === 5) {
          acc.priority += 1;
        }

        if (evaluationValue === 4) {
          acc.rejected += 1;
        }

        return acc;
      },
      {
        total: 0,
        unreviewed: 0,
        inProgress: 0,
        accepted: 0,
        priority: 0,
        rejected: 0,
      }
    );
  }, [contractOffers, evaluationStateByOfferId]);

  const handleEvaluationChange = useCallback(
    async (offerId: number, value: EvaluationValue) => {
      const previousValue = evaluationStateRef.current[offerId] ?? 0;

      if (previousValue === value) {
        return;
      }

      setEvaluationStateByOfferId((prev) => ({
        ...prev,
        [offerId]: value,
      }));

      try {
        await setEvaluationMutation({ offerId, value });
      } catch (error) {
        setEvaluationStateByOfferId((prev) => ({
          ...prev,
          [offerId]: previousValue,
        }));

        message.error(
          intl.formatMessage({
            id: 'evaluation-offer-decision-update-failed',
          })
        );
      }
    },
    [setEvaluationMutation, intl]
  );

  const handleFinalizeEvaluation = () => {
    Modal.confirm({
      title: intl.formatMessage({
        id: 'evaluation-finalize-confirm-title',
      }),
      icon: <ExclamationCircleOutlined />,
      content: intl.formatMessage({
        id: 'evaluation-finalize-confirm-content',
      }),
      okText: intl.formatMessage({
        id: 'evaluation-finalize-confirm-ok',
      }),
      okButtonProps: { danger: false },
      cancelText: intl.formatMessage({ id: 'cancel' }),
      onOk: () => finalizeEvaluationMutation(),
    });
  };

  // const handleSkipRemaining = () => {
  //   Modal.info({
  //     title: intl.formatMessage({
  //       id: 'evaluation-skip-placeholder-title',
  //       defaultMessage: 'Skip remaining',
  //     }),
  //     content: intl.formatMessage({
  //       id: 'evaluation-skip-placeholder-content',
  //       defaultMessage:
  //         'This is a UI placeholder. Skip remaining API will be connected later.',
  //     }),
  //     okText: intl.formatMessage({ id: 'ok' }),
  //   });
  // };

  // const handleSelectAllAsWinner = () => {
  //   Modal.info({
  //     title: intl.formatMessage({
  //       id: 'evaluation-select-all-placeholder-title',
  //       defaultMessage: 'Select all',
  //     }),
  //     content: intl.formatMessage({
  //       id: 'evaluation-select-all-placeholder-content',
  //       defaultMessage:
  //         'This is a UI placeholder. Bulk selection API will be connected later.',
  //     }),
  //     okText: intl.formatMessage({ id: 'ok' }),
  //   });
  // };

  const filterContent = (
    <div css={styles.filterWrapper}>
      {EVALUATION_FILTER_OPTIONS.map((option) => {
        const checked =
          option.value === 'all'
            ? isAllFiltersSelected
            : filterValues.includes(option.value);

        const color = getFilterColor(option.value);

        return (
          <div css={styles.filterItemWrapper} key={option.value}>
            <div css={styles.filterIconWrapper}>
              <span css={[styles.filterStatusIcon, { color }]}>
                {FILTER_ICON_BY_VALUE[option.value]}
              </span>

              <span>
                {intl.formatMessage({
                  id: option.intlId,
                })}
              </span>
            </div>

            <Checkbox
              checked={checked}
              onChange={(e) =>
                handleToggleFilter(option.value, e.target.checked)
              }
            />
          </div>
        );
      })}
    </div>
  );

  const isEmpty = contractOffers.length === 0;

  return (
    <div css={styles.wrapper}>
      <div css={styles.header}>
        <div>
          <div css={styles.eyebrow}>
            {intl.formatMessage({
              id: 'evaluation-process-title',
            })}
          </div>

          <div css={styles.description}>
            {intl.formatMessage({
              id: 'evaluation-process-description',
            })}
          </div>
        </div>

        <div css={styles.headerActions}>
          {/* <Button disabled={!!isEvaluationLocked} onClick={handleSkipRemaining}>
            {intl.formatMessage({
              id: 'evaluation-skip-button',
            })}
          </Button>

          <Button
            disabled={!!isEvaluationLocked}
            onClick={handleSelectAllAsWinner}>
            {intl.formatMessage({
              id: 'evaluation-select-all-button',
            })}
          </Button> */}

          <Button
            type="primary"
            disabled={!!isEvaluationLocked}
            loading={isFinalizingEvaluation}
            onClick={handleFinalizeEvaluation}>
            {intl.formatMessage({
              id: 'evaluation-finalize-button',
            })}
          </Button>
        </div>
      </div>

      {isEvaluationLocked && (
        <div css={styles.contractBlock}>
          <Tag color="orange">
            {intl.formatMessage({
              id: 'evaluation-locked-read-only',
            })}
          </Tag>
        </div>
      )}

      <div css={styles.summaryGrid}>
        <div css={styles.summaryCard}>
          <div css={[styles.summaryIcon, styles.totalIcon]}>
            <ProfileOutlined />
          </div>
          <div>
            <div css={styles.summaryValue}>{summary.total}</div>
            <div css={styles.summaryLabel}>
              {intl.formatMessage({
                id: 'evaluation-summary-total',
              })}
            </div>
          </div>
        </div>

        <div css={styles.summaryCard}>
          <div css={[styles.summaryIcon, styles.unreviewedIcon]}>
            <ClockCircleOutlined />
          </div>
          <div>
            <div css={styles.summaryValue}>{summary.unreviewed}</div>
            <div css={styles.summaryLabel}>
              {intl.formatMessage({
                id: 'evaluation-summary-unreviewed',
              })}
            </div>
          </div>
        </div>

        <div css={styles.summaryCard}>
          <div css={[styles.summaryIcon, styles.inProgressIcon]}>
            <StarOutlined />
          </div>
          <div>
            <div css={styles.summaryValue}>{summary.inProgress}</div>
            <div css={styles.summaryLabel}>
              {intl.formatMessage({
                id: 'evaluation-summary-in-progress',
              })}
            </div>
          </div>
        </div>

        <div css={styles.summaryCard}>
          <div css={[styles.summaryIcon, styles.acceptedIcon]}>
            <CheckCircleOutlined />
          </div>
          <div>
            <div css={styles.summaryValueLine}>
              <span css={styles.summaryValue}>{summary.accepted}</span>
              <span css={styles.summarySubValue}>
                {intl.formatMessage(
                  {
                    id: 'evaluation-summary-priority-value',
                  },
                  { priority: summary.priority }
                )}
              </span>
            </div>
            <div css={styles.summaryLabel}>
              {intl.formatMessage({
                id: 'evaluation-summary-accepted',
              })}
            </div>
          </div>
        </div>

        <div css={styles.summaryCard}>
          <div css={[styles.summaryIcon, styles.rejectedIcon]}>
            <StopOutlined />
          </div>
          <div>
            <div css={styles.summaryValue}>{summary.rejected}</div>
            <div css={styles.summaryLabel}>
              {intl.formatMessage({
                id: 'evaluation-summary-rejected',
              })}
            </div>
          </div>
        </div>
      </div>

      <div css={styles.toolbar}>
        <div css={styles.toolbarTitle}>
          <FilterOutlined />
          <span>
            {intl.formatMessage({
              id: 'evaluation-filter-deliveries-title',
            })}
          </span>
        </div>

        <Popover placement="bottomLeft" content={filterContent} trigger="click">
          <Button size="small" css={styles.filterButton}>
            <FilterOutlined />
            <span css={styles.filterButtonText}>{filterButtonLabel}</span>
          </Button>
        </Popover>

        <Tooltip
          title={intl.formatMessage({ id: 'offers-bulk-download-tooltip' })}>
          <Button
            size="small"
            css={styles.downloadAllButton}
            icon={<DownloadOutlined />}
            loading={isBulkDownloading}
            onClick={handleBulkDownload}>
            {intl.formatMessage({ id: 'offers-bulk-download-label' })}
          </Button>
        </Tooltip>

        <div css={styles.toolbarCounter}>
          {intl.formatMessage(
            {
              id: 'evaluation-showing-counter',
            },
            {
              current: filteredOffers.length,
              total: contractOffers.length,
            }
          )}
        </div>
      </div>

      {isEmpty ? (
        <div css={styles.emptyBlock}>
          <Empty
            description={intl.formatMessage({
              id: 'evaluation-empty-deliveries',
            })}
          />
        </div>
      ) : (
        <div css={styles.list}>
          {filteredOffers.map((offer) => {
            const value = evaluationStateByOfferId[offer.id] ?? 0;
            const option = getEvaluationOption(value);

            return (
              <div
                key={offer.id}
                css={[
                  styles.rowFrame,
                  {
                    borderLeftColor: option.color,
                  },
                ]}>
                <OffersEvaluationRow
                  projectId={projectId}
                  tenderId={tenderId}
                  contractId={contract.id}
                  contractName={contract.name || ''}
                  offer={offer}
                  serial={getOfferSerial(offer.id, serialMap)}
                  vouchers={vouchers}
                  responses={getOfferResponses(offer.id)}
                  evaluationValue={value}
                  onEvaluationChange={handleEvaluationChange}
                  isEvaluationLocked={isEvaluationLocked}
                />
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

const styleFn: ThemedStyleFn = () => ({
  wrapper: {
    display: 'flex',
    flexDirection: 'column',
    gap: 16,
    paddingTop: 20,
    width: '100%',
  },

  header: {
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    gap: 24,
  },

  eyebrow: {
    color: '#722ed1',
    fontSize: 12,
    fontWeight: 700,
    textTransform: 'uppercase',
    letterSpacing: 0.4,
    marginBottom: 4,
  },

  description: {
    marginTop: 10,
    maxWidth: 780,
    color: '#666666',
    fontSize: 14,
    lineHeight: 1.6,
  },

  headerActions: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    flexShrink: 0,
  },

  contractBlock: {
    padding: 16,
    border: '1px solid #e8e8e8',
    borderRadius: 6,
    background: '#fafafa',
  },

  summaryGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(5, minmax(120px, 1fr))',
    gap: 12,
  },

  summaryCard: {
    display: 'flex',
    alignItems: 'center',
    gap: 12,
    padding: 14,
    border: '1px solid #e8e8e8',
    borderRadius: 6,
    background: '#ffffff',
  },

  summaryIcon: {
    width: 34,
    height: 34,
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: 16,
  },

  totalIcon: {
    background: '#e6f7ff',
    color: '#1890ff',
  },

  unreviewedIcon: {
    background: '#fafafa',
    color: '#8c8c8c',
  },

  inProgressIcon: {
    background: '#e6f7ff',
    color: '#1890ff',
  },

  acceptedIcon: {
    background: '#f6ffed',
    color: '#52c41a',
  },

  rejectedIcon: {
    background: '#fff1f0',
    color: '#ff4d4f',
  },

  summaryValueLine: {
    display: 'flex',
    alignItems: 'baseline',
    gap: 6,
    flexWrap: 'wrap',
  },

  summarySubValue: {
    color: '#777777',
    fontSize: 12,
    fontWeight: 600,
  },

  summaryValue: {
    fontSize: 20,
    fontWeight: 700,
    lineHeight: 1,
    color: '#222222',
  },

  summaryLabel: {
    marginTop: 4,
    color: '#777777',
    fontSize: 12,
  },

  toolbar: {
    display: 'flex',
    alignItems: 'center',
    gap: 12,
    padding: '10px 12px',
    border: '1px solid #e8e8e8',
    borderRadius: 6,
    background: '#ffffff',
  },

  toolbarTitle: {
    display: 'flex',
    alignItems: 'center',
    gap: 6,
    fontWeight: 700,
    color: '#333333',
  },

  filterButton: {
    display: 'inline-flex',
    alignItems: 'center',
    maxWidth: 260,
    minWidth: 154,
    height: 28,
    padding: '0 10px',
  },

  downloadAllButton: {
    height: 28,
    display: 'inline-flex',
    alignItems: 'center',
    gap: 4,
    padding: '0 10px',
    whiteSpace: 'nowrap',
  },

  filterButtonText: {
    display: 'inline-block',
    marginLeft: 6,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },

  filterWrapper: {
    minWidth: 210,
    padding: '6px 0',
  },

  filterItemWrapper: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 18,
    padding: '6px 10px',
    color: '#222222',
    fontSize: 14,
    lineHeight: 1.2,

    '&:hover': {
      background: '#fafafa',
    },

    '.ant-checkbox-wrapper': {
      marginLeft: 0,
    },
  },

  filterIconWrapper: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    minWidth: 0,
    whiteSpace: 'nowrap',
  },

  filterStatusIcon: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: 14,
    minWidth: 14,
    fontSize: 14,
  },

  toolbarCounter: {
    marginLeft: 'auto',
    color: '#777777',
    fontSize: 12,
  },

  list: {
    display: 'flex',
    flexDirection: 'column',
    gap: 12,
  },

  rowFrame: {
    borderLeft: '3px solid transparent',
    borderRadius: 6,
  },

  emptyBlock: {
    padding: '40px 0',
    border: '1px solid #e8e8e8',
    borderRadius: 6,
    background: '#ffffff',
  },
});
